<template>
  <svg
    :style="computedStyle"
    :class="[
      'Icon',
      {
        [`Icon--${size}`]: size,
        [`Icon--${color}`]: color,
        ['Icon--flip-icon']: flipRtl,
      },
    ]"
  >
    <use :href="`${spriteHref}#${icon}`" />
  </svg>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';
import type { IconProps } from './Icon.model';
// Bundled so <use> gets a same-origin URL; layers can replace it, see docs/how-to-update-icons.md.
import spriteHref from '#cvo-icons/sprite.svg?no-inline';

const props = withDefaults(defineProps<IconProps>(), {
  size: 'md',
  clickable: false,
  flipRtl: false,
});

const computedStyle = computed((): CSSProperties => {
  const style: CSSProperties = {};

  if (typeof props.size === 'number') {
    style.width = `${props.size}px`;
    style.height = `${props.size}px`;
  }

  if (props.clickable) {
    style.cursor = 'pointer';
  }

  return style;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';

/* Available CSS custom properties:
 * --icon-size: overrides the icon width and height dimensions
 */

@layer cvo-ui {
  .Icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @include layout.square(var(--icon-size, var(--icon-default-size, 100%)));

    > path {
      fill: currentColor !important;
    }

    &--flip-icon {
      transform: scaleX(var(--transform-direction, 1));
    }

    /* Size variants */
    &--xs {
      --icon-default-size: 12px;
    }

    &--sm {
      --icon-default-size: 16px;
    }

    &--md {
      --icon-default-size: 20px;
    }

    &--lg {
      --icon-default-size: 24px;
    }

    &--xl {
      --icon-default-size: 32px;
    }

    &--info {
      color: vars.$color-system-status-info-900;
    }

    &--success {
      color: vars.$color-system-status-success-900;
    }

    &--warning {
      color: vars.$color-system-status-warning-900;
    }

    &--danger {
      color: vars.$color-system-status-danger-900;
    }

    &--primary {
      color: vars.$color-brand-primary-900;
    }

    &--secondary {
      color: vars.$color-brand-secondary-900;
    }

    &--accent {
      color: vars.$color-brand-accent-900;
    }

    /* Fallback state */
    &--fallback {
      background-color: vars.$color-neutral-gray-100;
      color: vars.$color-neutral-gray-700;
      border-radius: vars.$border-radius-xs;
      font-size: 0.8em;
      font-weight: vars.$typography-weight-strong;
    }
  }
}
</style>
